import React, { useState } from 'react';
import { Dialog, DialogActions, DialogContent } from '@mui/material';
import MuiPhoneNumber from 'mui-phone-number';
import PrimaryButton from '../../common/components/PrimaryButton';
import TertiaryButton from '../../common/components/TertiaryButton';
import PartnerLogoFooter from './PartnerLogoFooter';
import { testimonialAuthor, testimonialDescription } from './testimonial';

type Props = {
    isOpen: boolean;
    handleClose: () => void;
    startTrial: (phoneNumber: string, country: string) => void;
};

// The package typings omit the (value, country) onChange signature
const PhoneField = MuiPhoneNumber as React.ComponentType<any>;

const HERO_IMAGE = require('../assets/Rectangle-1.png');

const DEFAULT_COUNTRY = 'US';
const DEFAULT_DIAL_CODE = '1';

export default function StartCoachTrialDialog({ isOpen, handleClose, startTrial }: Props) {
    const [phoneNumber, setPhoneNumber] = useState('');
    const [dialCode, setDialCode] = useState(DEFAULT_DIAL_CODE);
    const [countryCode, setCountryCode] = useState(DEFAULT_COUNTRY);

    const nationalNumberLength = phoneNumber.replace(/\D/g, '').length - dialCode.length;
    // The phone number is optional, so an empty field is valid
    const phoneIsValid = nationalNumberLength <= 0 || (nationalNumberLength >= 7 && nationalNumberLength <= 12);
    const phoneError = !phoneIsValid;

    const handleStartTrial = () => {
        const formattedNumber = nationalNumberLength > 0 ? phoneNumber.replace(/[()\-+ ]+/g, '') : '';
        startTrial(formattedNumber, countryCode);
    };

    return (
        <Dialog
            data-testid="dialog"
            open={isOpen}
            onClose={handleClose}
            aria-labelledby="start-trial-title"
            fullWidth
            maxWidth={false}
            PaperProps={{ sx: { width: 'calc(100% - 40px)', height: 'calc(100% - 40px)', m: '20px' } }}
        >
            <div className="trial-dialog-layout">
                <div className="trial-dialog-form">
                    <DialogContent>
                        <div className="title-container">
                            <div id="start-trial-title" className="title">
                                Start your free
                                <br /> 14-day coach trial
                            </div>
                            <img
                                alt="highlight"
                                className="title-highlight"
                                height={50}
                                width={350}
                                style={{ maxWidth: '100%' }}
                                src="https://static.trainheroic.com/icons/Highlight.Long2.png"
                            />
                        </div>
                        <div className="form-input">
                            <ul>
                                <li>Lorem ipsum dolor sit amet, consectetur adipiscing elit.</li>
                                <li>Sed do eiusmod tempor incididunt ut labore et dolore magna aliqua.</li>
                                <li>Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris.</li>
                            </ul>
                            <PhoneField
                                data-testid="text-field-input"
                                className="form-textfield"
                                inputProps={{ name: 'phone' }}
                                InputLabelProps={{
                                    classes: {
                                        root: 'input',
                                        asterisk: 'label-asterisk',
                                        focused: 'input-focus'
                                    }
                                }}
                                type="tel"
                                size="small"
                                variant="standard"
                                label="Phone Number (optional)"
                                value={phoneNumber}
                                defaultCountry="us"
                                disableAreaCodes={true}
                                error={phoneError}
                                helperText={phoneError ? 'Invalid phone number' : ''}
                                onChange={(value: string, country: { countryCode: string; dialCode: string }) => {
                                    setCountryCode(country.countryCode.toUpperCase());
                                    setDialCode(country.dialCode);
                                    setPhoneNumber(value);
                                }}
                            />
                        </div>
                    </DialogContent>
                    <DialogActions>
                        <div className="action-buttons-container" style={{ width: '100%' }}>
                            <PrimaryButton disabled={!phoneIsValid} onClick={handleStartTrial}>
                                Start Your Free Trial
                            </PrimaryButton>
                            <div className="cta-reassurance">No credit card required &bull; Cancel anytime</div>
                            <TertiaryButton onClick={handleClose}>Cancel</TertiaryButton>
                        </div>
                    </DialogActions>
                </div>
                <div className="trial-dialog-testimonial">
                    <img alt="" className="trial-dialog-hero" src={HERO_IMAGE} />
                    <div className="trial-dialog-testimonial-body">
                        <div className="section-card testimonial-card">
                            <h4 className="testimonial-content">{testimonialDescription}</h4>
                            <h6 className="testimonial-author">{testimonialAuthor}</h6>
                        </div>
                    </div>
                    <div className="trial-dialog-logos">
                        <PartnerLogoFooter />
                    </div>
                </div>
            </div>
        </Dialog>
    );
}
